import { GraphQLQuery, GraphQLResult } from '@aws-amplify/api';
import Axios, { AxiosRequestConfig } from 'axios';
import logger from 'src/log/log';
import { getBackendUrl } from '../config';
import { authManager } from '../utils/auth';
import { getJwtToken } from './auth/index';
import toast from 'react-hot-toast';

const apiErrorHandler = (error: any) => {
  logger.debug('apiErrorHandler', error);
  return error;
};
export default class PlanvasBaseApi {
  apiEndpoint = getBackendUrl();

  async recursiveGet<T, T1>(accessor, fn: (request?: T1) => Promise<GraphQLResult<GraphQLQuery<T>>>, initialPayload) {
    const response = await fn(initialPayload);
    if (response.data?.[accessor]?.nextToken) {
      const nextResponse = await fn({ ...initialPayload, nextToken: response.data[accessor].nextToken });
      response.data[accessor].items = response.data[accessor].items.concat(nextResponse?.data?.[accessor]?.items || []);
    }
    return response as GraphQLResult<GraphQLQuery<T>>;
  }

  /**
   * Iterates through all pages of a paginated GraphQL query and aggregates the results
   * @param query The GraphQL query string
   * @param accessor The field name in the response data that contains items and nextToken
   * @param limit Optional limit per page (default: 100)
   * @returns Promise with all items aggregated
   */
  async iterateGraphQLQuery<T>(
    query: string,
    accessor: string,
    limit: number = 100,
  ): Promise<GraphQLResult<GraphQLQuery<T>>> {
    const { API, graphqlOperation } = await import('aws-amplify');
    let allItems: any[] = [];
    let nextToken: string | null | undefined = undefined;
    let pageCount = 0;
    let originalTypename: string | undefined = undefined;

    do {
      const variables = { limit, ...(nextToken && { nextToken }) };
      const response = await API.graphql<GraphQLQuery<T>>(graphqlOperation(query, variables));

      const data = response.data?.[accessor];
      if (!data) {
        console.warn(`No data found for accessor: ${accessor}`);
        break;
      }

      // Capture the original __typename from the first response
      if (!originalTypename && data.__typename) {
        originalTypename = data.__typename;
      }

      const items = data.items || [];
      allItems = [...allItems, ...items];
      nextToken = data.nextToken;
      pageCount++;

      // Log pagination progress
      logger.debug(
        `[iterateGraphQLQuery] Fetched page ${pageCount}: ${items.length} items, total: ${allItems.length}, nextToken: ${nextToken ? 'present' : 'none'}`,
      );
    } while (nextToken);

    // Return in the expected format with all items aggregated
    return {
      data: {
        [accessor]: {
          items: allItems,
          nextToken: null,
          __typename:
            originalTypename ||
            `Model${accessor.replace('list', '').charAt(0).toUpperCase() + accessor.replace('list', '').slice(1)}Connection`,
        },
      },
    } as GraphQLResult<GraphQLQuery<T>>;
  }

  private async getToken(): Promise<string> {
    return getJwtToken();
  }

  private getHeaders(jwtToken: string) {
    return {
      Authorization: 'AWS ' + jwtToken,
    };
  }

  onFailed = (err) => {
    authManager.handleError(err);
    logger.error(err);
    // toast.error(`We're having trouble accessing the server. Please try again later.`);
    return apiErrorHandler(err);
  };

  postWithProgress(endpoint, body, headers, onUploadProgress, options?: AxiosRequestConfig) {
    return this.post(endpoint, body, headers, { onUploadProgress, ...options });
  }

  post(endpoint, body, headers = {}, options: AxiosRequestConfig = {}) {
    return this.getToken().then((jwtToken) => {
      return Axios({
        method: 'POST',
        headers: { ...this.getHeaders(jwtToken), ...headers },
        data: body,
        url: this.apiEndpoint + endpoint,
        ...options,
      })
        .then((res) => apiErrorHandler(res.data))
        .catch(this.onFailed);
    });
  }

  get(endpoint, queryStringParameters = {}, options: AxiosRequestConfig = {}) {
    return this.getToken().then((jwtToken) => {
      return Axios({
        method: 'GET',
        headers: this.getHeaders(jwtToken),
        params: queryStringParameters,
        url: this.apiEndpoint + endpoint,
        ...options,
      })
        .then((res) => apiErrorHandler(res.data))
        .catch(this.onFailed);
    });
  }

  getBlob(endpoint, queryStringParameters, options: AxiosRequestConfig = {}) {
    return this.getToken().then((jwtToken) => {
      return Axios({
        method: 'GET',
        headers: this.getHeaders(jwtToken),
        params: queryStringParameters,
        url: this.apiEndpoint + endpoint,
        responseType: 'blob',
        ...options,
      })
        .then((res) => apiErrorHandler(res.data))
        .catch(this.onFailed);
    });
  }

  put(endpoint, body, options: AxiosRequestConfig = {}) {
    return this.getToken().then((jwtToken) => {
      return Axios({
        method: 'PUT',
        headers: this.getHeaders(jwtToken),
        data: body,
        url: this.apiEndpoint + endpoint,
        ...options,
      })
        .then((res) => apiErrorHandler(res.data))
        .catch(this.onFailed);
    });
  }

  async patch(path: string, data?: any, config?: AxiosRequestConfig) {
    return this.request('PATCH', path, data, config);
  }

  async delete(path: string, data?: any, config?: AxiosRequestConfig) {
    return this.request('DELETE', path, data, config);
  }

  async request(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    path: string,
    data?: any,
    config?: AxiosRequestConfig,
  ) {
    return this.getToken().then((jwtToken) => {
      return Axios({
        method,
        headers: this.getHeaders(jwtToken),
        data,
        url: this.apiEndpoint + path,
        ...config,
      })
        .then((res) => apiErrorHandler(res.data))
        .catch(this.onFailed);
    });
  }
}
